import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
    AbgleichKennzahlen,
    AbgleichZeile,
    AllgemeinerHinweis,
    AusgeschlossenesGewerk,
    GewerkAusPlan,
    MatrixDaten,
} from '../../components/koordinierung/koordinierungLogic';
import {
    bestaetigteBewertungen,
    gewerkeMitLuecke,
} from '../../components/koordinierung/koordinierungLogic';
import { formatDateLocal } from '../dateService';
import {
    A4_GEOMETRY,
    CompanyInfo,
    cityFromAddress,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    FOOTER_RESERVE,
    longDate,
} from './letterhead';
import { COLORS } from './pdfUtils';
import { sichereTextausgabe, winAnsiSicher } from './winAnsi';

/**
 * Der Koordinierungsbericht: was die Gewerke-Ueberschneidung fuer dieses
 * Projekt ergeben hat.
 *
 * Ein EIGENSTAENDIGES Dokument, kein Anhang an den Plotterplan. Der Plan sagt,
 * was wann gemacht wird; dieses Blatt sagt, wo sich die Gewerke dabei
 * begegnen, was daraus folgt und wer das festgestellt hat.
 *
 * ═══ WAS AUF DEM BLATT STEHEN MUSS ═══════════════════════════════════════
 * Nicht nur die bestaetigten Festlegungen. Ein Bericht, der ausschliesslich
 * das Geregelte zeigt, wird als „geprueft und ohne weiteren Befund" gelesen —
 * und genau das waere falsch, solange Paare unbewertet und Gewerke ohne
 * Zeitraum oder ohne Zuordnung offen sind. Deshalb tragen alle vier Teile das
 * Blatt:
 *   1. die Matrix (wer trifft wann auf wen),
 *   2. die bestaetigten Festlegungen,
 *   3. die OFFENEN Paare,
 *   4. die Gewerke, die aus der Pruefung herausfallen (ohne Zeitraum, ohne
 *      Zuordnung).
 * Dazu Katalogfassung und Stand — waechst der Katalog spaeter, bleibt dieses
 * Blatt nachvollziehbar.
 *
 * ─── Briefkopf ────────────────────────────────────────────────────────────
 * Ausschliesslich ueber `letterhead.ts` (createLetterhead / drawPage1Header /
 * drawFooters), wie Rechnung, Angebot, Einweisung und Klaerungsliste.
 * Umgeblaettert wird nur ueber `lh.addPage()`, damit ein hinterlegtes
 * Briefpapier auf JEDER Seite landet; die Tabellen bekommen zusaetzlich
 * `willDrawPage`, weil autoTable seine Folgeseiten selbst anlegt.
 */

// ─── Typografie ─────────────────────────────────────────────────────────────

const SCHRIFT = {
    titel: 15,
    untertitel: 10.5,
    kapitel: 11,
    block: 9.5,
    klein: 8,
    matrix: 6.5,
};

const ZEILE = {
    text: 4.4,
    klein: 3.4,
    kapitel: 5.4,
};

/** Ueberschrift des Blattes — auch fuer Dateinamen und Betreffzeilen. */
export const KOORDINIERUNG_TITEL = 'Koordinierung der Gewerke';

/**
 * Hoechstzahl der Gewerke, die als Matrix aufs Blatt passen.
 *
 * Rechnung: 170 mm Satzspiegel minus 42 mm Namensspalte = 128 mm fuer n
 * Spalten. Unter 6 mm Spaltenbreite ist die Zahl in der Zelle nicht mehr zu
 * lesen — 128 / 6 = 21, abgerundet auf 20.
 *
 * Bei mehr Gewerken wird die Matrix WEGGELASSEN und das ausdruecklich
 * vermerkt. Eine unlesbar gequetschte Matrix waere schlimmer als keine: sie
 * saehe aus wie eine Aussage.
 */
export const MATRIX_MAX_GEWERKE = 20;

/** Breite der Namensspalte der Matrix in mm. */
const MATRIX_NAME_W = 42;

/** Hoehe einer Matrixzeile in mm. */
const MATRIX_ZEILE_H = 5.2;

const AMPEL_FARBE: Record<string, [number, number, number]> = {
    rot: [225, 29, 72],
    orange: [252, 211, 77],
    grau: [248, 250, 252],
};

export interface KoordinierungPdfDaten {
    gewerke: GewerkAusPlan[];
    matrix: MatrixDaten;
    /** Bereits sortiert (Schwere, dann Dauer) — die Reihenfolge des Blattes
     *  soll der der Oberflaeche entsprechen. */
    zeilen: AbgleichZeile[];
    ohneZeitraum: AusgeschlossenesGewerk[];
    offeneGewerke: GewerkAusPlan[];
    hinweise: AllgemeinerHinweis[];
    kennzahlen: AbgleichKennzahlen;
    katalogVersion: string;
    /** Fassung des SiGe-Plans, aus dem gerechnet wurde. */
    planStand: string;
}

export interface KoordinierungPdfOptionen {
    projektName?: string;
    projektNummer?: string;
    bauvorhabenAdresse?: string;
    /** Ort/Datum-Zeile rechts. */
    ort?: string;
    datumIso?: string;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten.
     *
     * Nur fuer Tests: nur so laesst sich pruefen, dass das Briefpapier auf
     * JEDER Seite genau einmal gestempelt wird. Dasselbe Muster wie
     * `__docFactory` im Rechnungs-, Einweisungs- und Klaerungslisten-Generator.
     */
    __docFactory?: () => jsPDF;
}

// ─── Kleinkram ──────────────────────────────────────────────────────────────

/**
 * Mehrzeiliges Feld zu einer Zeile — Kopfangaben sind einzeilig.
 *
 * Zieht zugleich die Kodierung gerade. Das geschieht HIER und nicht erst in
 * `sichereTextausgabe`, weil `splitTextToSize` und die Spaltenbreiten von
 * `jspdf-autotable` sonst eine andere Zeichenkette ausmessen als die, die
 * spaeter gesetzt wird. Warum ueberhaupt: `winAnsi.ts` — EIN Zeichen
 * ausserhalb von CP1252 kippt die GANZE Zeichenkette in UTF-16, und aus einer
 * Gefaehrdungsbeschreibung wird unlesbarer Zeichensalat.
 */
const einzeilig = (v?: string | null): string =>
    winAnsiSicher((v == null ? '' : String(v)).replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim());

/**
 * ISO-Tagesdatum als deutsches Datum, rein zeichenweise.
 *
 * Kein `new Date(...)`: dessen Ergebnis haengt an der Zeitzone der Laufzeit,
 * und ein Ueberschneidungsbeginn, der im Bericht einen Tag danebenliegt, ist
 * schlimmer als gar keiner. Dieselbe Ueberlegung traegt die Rechenlogik
 * (`ueberschneidung.ts`).
 */
const tag = (iso?: string | null): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '—';

/**
 * Das heutige Kalenderdatum — OERTLICH, nicht in UTC.
 *
 * `new Date().toISOString().slice(0, 10)` liefert den UTC-Tag: zwischen 00:00
 * und 02:00 MESZ traegt der Bericht damit den Vortag, und derselbe Fehler stuende
 * dann im Dateinamen. Das faellt in einem Modul auf, das `Date` sonst ueberall
 * verbannt (siehe `tag` oben und `ueberschneidung.ts`).
 *
 * Der Zeitpunkt ist uebergebbar, damit sich genau dieser Fall pruefen laesst.
 */
export const heuteAlsIso = (jetzt: Date = new Date()): string => formatDateLocal(jetzt);

const PRIO_TEXT: Record<string, string> = { hoch: 'Hoch', mittel: 'Mittel', niedrig: 'Gering' };

// ─── Der Satz ───────────────────────────────────────────────────────────────

const renderKoordinierung = (
    doc: jsPDF,
    daten: KoordinierungPdfDaten,
    company: CompanyInfo,
    optionen?: KoordinierungPdfOptionen,
): void => {
    const opt: KoordinierungPdfOptionen = optionen || {};
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    let y = 0;
    const ensureSpace = createSpaceGuard(lh, geo, () => y, v => { y = v; });

    /**
     * Platz fuer einen Block der Hoehe `hoehe` — oder eine neue Seite.
     *
     * `createSpaceGuard` misst gegen die BLATTKANTE und klemmt nur gegen den
     * Fusszeilenstreifen ab. Ein mehrzeiliger Block muss seine eigene Hoehe
     * deshalb draufrechnen, sonst beginnt er zwar noch auf der Seite, seine
     * letzten Zeilen landen aber in der Fusszeile.
     */
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;
    const platzFuer = (hoehe: number) => ensureSpace(hoehe + fussReserve);
    const nutzbareHoehe = geo.pageHeight - lh.contentTopFollowing - fussReserve;

    // ── Ort und Datum ────────────────────────────────────────────────────
    // Kein Anschriftfeld: der Koordinierungsbericht ist eine Feststellung fuer
    // die Akte und kein Anschreiben. Wer ihn verschickt, legt ihn einer Mail
    // oder einem Protokoll bei.
    y = Math.max(addressFieldY, rightColBottom + 8);

    const datumIso = (opt.datumIso || '').slice(0, 10) || heuteAlsIso();
    const ort = (opt.ort || '').trim() || cityFromAddress(company ? company.address : undefined);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    doc.setTextColor(tp0, tp1, tp2);
    const datumText = longDate(datumIso);
    doc.text(ort ? `${ort}, ${datumText}` : datumText, rightX, y, { align: 'right' });
    y += 12;

    // ── Titel ────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.text(KOORDINIERUNG_TITEL, margin, y);
    y += 7;

    const untertitel: string[] = [];
    const projektName = einzeilig(opt.projektName);
    const projektNummer = einzeilig(opt.projektNummer);
    if (projektName && projektNummer) untertitel.push(`${projektName} · Projekt-Nr. ${projektNummer}`);
    else if (projektName) untertitel.push(projektName);
    else if (projektNummer) untertitel.push(`Projekt-Nr. ${projektNummer}`);

    const bauvorhaben = einzeilig(opt.bauvorhabenAdresse);
    if (bauvorhaben) untertitel.push(`Bauvorhaben: ${bauvorhaben}`);
    untertitel.push('Zeitliche Überschneidung der Gewerke nach § 3 Abs. 3 BaustellV');

    const stand = einzeilig(daten.planStand);
    // Katalogfassung und Planstand gehoeren AUF das Blatt: waechst der Katalog,
    // aendert sich nicht rueckwirkend, was hier galt — aber nur, wenn man
    // spaeter noch sieht, unter welcher Fassung geurteilt wurde.
    untertitel.push(
        `Stand: ${tag(datumIso)}${stand ? ` · Planfassung: ${stand}` : ''}`
        + ` · Katalogfassung: ${einzeilig(daten.katalogVersion) || '—'}`,
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    untertitel.forEach(zeile => {
        (doc.splitTextToSize(zeile, contentWidth) as string[]).forEach(z => {
            doc.text(z, margin, y);
            y += 5;
        });
    });

    y += 1.5;
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightX, y);
    y += 6;

    // ─────────────────────────────────────────────────────────────────────
    // Werkzeug
    // ─────────────────────────────────────────────────────────────────────

    const ueberschrift = (t: string) => {
        platzFuer(ZEILE.kapitel + 10);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.kapitel);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(t, margin, y);
        y += 5.5;
    };

    const absatz = (t: string, stil: 'normal' | 'italic' = 'normal', groesse = SCHRIFT.block) => {
        const roh = (t == null ? '' : String(t)).trim();
        if (!roh) return;
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        doc.setTextColor(tp0, tp1, tp2);
        const zeilen = doc.splitTextToSize(roh, contentWidth) as string[];
        zeilen.forEach(z => {
            platzFuer(ZEILE.text);
            doc.text(z, margin, y);
            y += ZEILE.text;
        });
    };

    const tabellenStil = {
        margin: lh.autoTableMargin(),
        willDrawPage: lh.willDrawPage,
        theme: 'grid' as const,
        // Ohne wiederholte Kopfzeile weiss der Leser auf Seite 4 nicht mehr,
        // welche Spalte die Maßnahme ist.
        showHead: 'everyPage' as const,
        headStyles: {
            fillColor: [255, 255, 255] as [number, number, number],
            textColor: [tp0, tp1, tp2] as [number, number, number],
            fontSize: SCHRIFT.klein,
            fontStyle: 'bold' as const,
            lineColor: [ln0, ln1, ln2] as [number, number, number],
            lineWidth: 0.2,
        },
        styles: {
            fontSize: SCHRIFT.klein,
            textColor: [tp0, tp1, tp2] as [number, number, number],
            lineColor: [ln0, ln1, ln2] as [number, number, number],
            lineWidth: 0.2,
            cellPadding: { top: 1.4, bottom: 1.4, left: 1.6, right: 1.6 },
            valign: 'top' as const,
            // Nichts wird abgeschnitten — eine halbe Maßnahme sagt etwas
            // anderes als die ganze.
            overflow: 'linebreak' as const,
        },
    };

    const nachTabelle = () => {
        const letzte = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;
        y = (letzte && typeof letzte.finalY === 'number' ? letzte.finalY : y) + 8;
    };

    // ─────────────────────────────────────────────────────────────────────
    // 1) Lage in Zahlen
    // ─────────────────────────────────────────────────────────────────────

    const kz = daten.kennzahlen;
    // Die Luecke gehoert in dieselbe Zeile wie die uebrigen Zahlen: ein Gewerk
    // mit undatiertem Arbeitsschritt steht in der Matrix und sieht dort
    // vollstaendig aus — es faellt sonst nirgends auf.
    const luecke = gewerkeMitLuecke(daten.gewerke || []);
    absatz(
        `${kz.gewerke} Gewerk(e) im Plan · ${kz.paare} Gewerkepaar(e) mit gemeinsamer Zeit · `
        + `${kz.bestaetigt} bestätigte Festlegung(en) · ${kz.unbewertet} Paar(e) noch nicht bewertet · `
        + `${kz.veraltet} überholt · ${kz.gewerkeOhneZeitraum} Gewerk(e) ohne verwertbaren Zeitraum · `
        + `${luecke.length} Gewerk(e) mit Arbeitsschritten ohne Datum · `
        + `${kz.nichtZugeordnet} Gewerk(e) ohne Zuordnung.`,
    );
    y += 4;

    // ─────────────────────────────────────────────────────────────────────
    // 2) Matrix
    // ─────────────────────────────────────────────────────────────────────

    const matrixGewerke = daten.matrix.gewerke;
    ueberschrift('Übersicht der Überschneidungen');

    if (matrixGewerke.length < 2) {
        absatz(
            'Es gibt weniger als zwei Gewerke mit vollständigem Zeitraum — eine Matrix lässt sich daraus '
            + 'nicht bilden. Das ist KEIN Nachweis der Gefährdungsfreiheit: fehlende Termine erzeugen den '
            + 'Eindruck, es gäbe keine Überschneidung.',
            'italic',
        );
        y += 4;
    } else if (matrixGewerke.length > MATRIX_MAX_GEWERKE) {
        // Ausdruecklich vermerken statt unlesbar quetschen: eine gequetschte
        // Matrix saehe aus wie eine Aussage.
        absatz(
            `Der Plan führt ${matrixGewerke.length} Gewerke mit Zeitraum. Eine Kreuzmatrix dieser Größe ist `
            + `auf A4 nicht mehr lesbar (Grenze: ${MATRIX_MAX_GEWERKE}); sie wurde deshalb weggelassen. `
            + 'Die nachfolgenden Listen sind vollständig und enthalten dieselben Paare.',
            'italic',
        );
        y += 4;
    } else {
        const n = matrixGewerke.length;
        const spalteW = (contentWidth - MATRIX_NAME_W) / n;
        const gesamtHoehe = MATRIX_ZEILE_H * (n + 1);

        // Die Matrix wird NICHT ueber einen Seitenumbruch getrennt: eine halbe
        // Matrix ohne Kopfzeile ist nicht zu lesen. Passt sie auf keine leere
        // Seite, bleibt sie trotzdem am Stueck stehen — die Grenze oben sorgt
        // dafuer, dass dieser Fall nicht eintritt.
        if (gesamtHoehe < nutzbareHoehe) platzFuer(gesamtHoehe + 4);

        // Kopfzeile: laufende Nummern. Ausgeschriebene Gewerksnamen brauchten
        // gedrehte Schrift oder 4 cm Spalten und waeren ab acht Gewerken
        // unlesbar; die Zeilen tragen den vollen Namen mit derselben Nummer.
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.matrix);
        doc.setTextColor(tp0, tp1, tp2);
        matrixGewerke.forEach((g, i) => {
            doc.text(
                String(i + 1),
                margin + MATRIX_NAME_W + spalteW * i + spalteW / 2,
                y + MATRIX_ZEILE_H - 1.6,
                { align: 'center' },
            );
        });
        y += MATRIX_ZEILE_H;

        matrixGewerke.forEach((a, i) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(SCHRIFT.matrix);
            doc.setTextColor(tp0, tp1, tp2);
            // Der Zusatz „ohne Datum" steht AM Gewerk und nicht nur in der
            // Liste darunter: die Zeile dieses Gewerks zeigt sonst graue Zellen,
            // und grau liest sich als „geprüft, keine gemeinsame Zeit".
            const name = `${i + 1}  ${a.label}${a.zugeordnet ? '' : ' (nicht zugeordnet)'}`
                + (a.schritteOhneDatum > 0 ? ` (${a.schritteOhneDatum} ohne Datum)` : '');
            const gekuerzt = (doc.splitTextToSize(name, MATRIX_NAME_W - 2) as string[])[0] || name;
            doc.text(gekuerzt, margin, y + MATRIX_ZEILE_H - 1.6);

            matrixGewerke.forEach((b, j) => {
                const x = margin + MATRIX_NAME_W + spalteW * j;
                const zelle = daten.matrix.zellen[i][j];
                if (zelle.diagonale) {
                    doc.setFillColor(226, 232, 240);
                    doc.rect(x, y, spalteW, MATRIX_ZEILE_H, 'F');
                    return;
                }
                const [r, g2, b2] = AMPEL_FARBE[zelle.ampel] || AMPEL_FARBE.grau;
                doc.setFillColor(r, g2, b2);
                doc.setDrawColor(ln0, ln1, ln2);
                doc.setLineWidth(0.1);
                doc.rect(x, y, spalteW, MATRIX_ZEILE_H, 'FD');
                if (zelle.dauerTage > 0) {
                    // Weiße Zahl auf Rot, dunkle sonst — sonst ist die Zahl in
                    // der roten Zelle nicht zu lesen.
                    if (zelle.ampel === 'rot') doc.setTextColor(255, 255, 255);
                    else doc.setTextColor(tp0, tp1, tp2);
                    doc.setFont('helvetica', zelle.status === 'unbewertet' ? 'bold' : 'normal');
                    doc.setFontSize(SCHRIFT.matrix);
                    doc.text(String(zelle.dauerTage), x + spalteW / 2, y + MATRIX_ZEILE_H - 1.6, { align: 'center' });
                }
            });
            y += MATRIX_ZEILE_H;
        });

        doc.setTextColor(tp0, tp1, tp2);
        y += 2;
        absatz(
            'Die Zahl in der Zelle ist die Zahl gemeinsamer Kalendertage; ein einziger gemeinsamer Tag '
            + 'zählt bereits. Rot ab mehr als 28 Tagen. Fett gesetzte Zahlen sind noch nicht bewertet. '
            + 'Eine leere Zelle heißt „keine gemeinsame Zeit" — bei einem Gewerk mit dem Vermerk '
            + '„ohne Datum" heißt sie das nur für die datierten Arbeitsschritte.',
            'italic',
            SCHRIFT.klein,
        );
        y += 5;
    }

    // ─────────────────────────────────────────────────────────────────────
    // 3) Bestaetigte Festlegungen
    // ─────────────────────────────────────────────────────────────────────

    const bestaetigt = bestaetigteBewertungen(daten.zeilen);
    ueberschrift(`Bestätigte Festlegungen (${bestaetigt.length})`);

    if (bestaetigt.length === 0) {
        absatz(
            'Es ist noch keine Festlegung bestätigt. Vorschläge aus dem Katalog sind keine Festlegung — '
            + 'sie werden erst durch die Bestätigung des Koordinators zu einer.',
            'italic',
        );
        y += 4;
    } else {
        // ═══ BESTAETIGT HEISST NICHT AKTUELL ═════════════════════════════
        // Eine bestaetigte Festlegung bleibt in dieser Tabelle stehen, auch
        // wenn der Plan die beiden Gewerke inzwischen verschoben hat oder gar
        // nicht mehr gleichzeitig fuehrt — geloescht wird sie nie. Ohne die
        // Spalte „Stand" laese sich der abgedruckte Zeitraum aber als der
        // heutige, und der Bericht behauptete eine Pruefung, die es fuer den
        // neuen Termin nicht gibt.
        const ueberholt = bestaetigt.filter(f => f.stand !== 'aktuell');
        if (ueberholt.length > 0) {
            absatz(
                `${ueberholt.length} der ${bestaetigt.length} bestätigten Festlegung(en) beziehen sich auf einen `
                + 'Terminstand, den der Plan nicht mehr hergibt. Sie bleiben hier stehen — dass die Gefährdung '
                + 'einmal erkannt und geregelt war, gehört in die Akte — und sind in der Spalte „Stand" '
                + 'gekennzeichnet. Der abgedruckte Zeitraum ist der bewertete, nicht der heutige.',
                'italic',
                SCHRIFT.klein,
            );
            y += 2;
        }
        autoTable(doc, {
            ...tabellenStil,
            startY: y,
            head: [['Gewerke', 'Zeitraum', 'Tage', 'Gefährdung', 'Koordinierende Maßnahme', 'Prio', 'Bestätigt', 'Stand']],
            body: bestaetigt.map(({ zeile, bewertung, standText }) => [
                `${zeile.aLabel}\n× ${zeile.bLabel}`,
                bewertung.alt.von || bewertung.alt.bis
                    ? `${tag(bewertung.alt.von)}\n– ${tag(bewertung.alt.bis)}`
                    : '—',
                bewertung.alt.dauerTage != null ? String(bewertung.alt.dauerTage) : '—',
                bewertung.zeile.gefaehrdung,
                bewertung.zeile.massnahme || '—',
                PRIO_TEXT[bewertung.zeile.prioritaet] || bewertung.zeile.prioritaet,
                `${tag((bewertung.zeile.bestaetigt_am || '').slice(0, 10))}\n${bewertung.zeile.bestaetigt_von_name || ''}`,
                standText,
            ]),
            columnStyles: {
                0: { cellWidth: 24 },
                1: { cellWidth: 19 },
                2: { cellWidth: 8, halign: 'center' },
                3: { cellWidth: 'auto' },
                4: { cellWidth: 38 },
                5: { cellWidth: 11 },
                6: { cellWidth: 20 },
                7: { cellWidth: 20 },
            },
        });
        nachTabelle();
    }

    // ─────────────────────────────────────────────────────────────────────
    // 4) Offene Paare
    // ─────────────────────────────────────────────────────────────────────

    const offene = (daten.zeilen || []).filter(
        z => z.status === 'unbewertet' || z.status === 'geaendert' || z.unbestaetigt > 0,
    );
    ueberschrift(`Noch offen (${offene.length})`);
    absatz(
        'Diese Paare treffen zeitlich aufeinander, ohne dass eine bestätigte Festlegung vorliegt. '
        + 'Sie stehen hier, damit das Blatt nicht als „geprüft und ohne Befund" gelesen wird.',
        'italic',
        SCHRIFT.klein,
    );
    y += 2;

    if (offene.length === 0) {
        absatz('Kein offenes Paar.', 'italic');
        y += 4;
    } else {
        autoTable(doc, {
            ...tabellenStil,
            startY: y,
            head: [['Gewerke', 'Zeitraum', 'Tage', 'Stand', 'Was der Katalog vorschlägt']],
            body: offene.map(z => [
                `${z.aLabel}\n× ${z.bLabel}`,
                z.berechnet ? `${tag(z.berechnet.von)}\n– ${tag(z.berechnet.bis)}` : '—',
                z.berechnet ? String(z.dauerTage) : '—',
                z.status === 'unbewertet'
                    ? 'nicht bewertet'
                    : z.status === 'geaendert'
                        ? 'Zeitraum verschoben'
                        : `${z.unbestaetigt} nicht bestätigt`,
                z.vorschlaege.length > 0
                    ? z.vorschlaege.map(v => `${v.code}: ${v.gefaehrdung}`).join('\n')
                    : 'Der Katalog kennt zu diesem Paar keine Regel.',
            ]),
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 20 },
                2: { cellWidth: 9, halign: 'center' },
                3: { cellWidth: 26 },
                4: { cellWidth: 'auto' },
            },
        });
        nachTabelle();
    }

    // ─────────────────────────────────────────────────────────────────────
    // 5) Gewerke ohne verwertbaren Zeitraum
    // ─────────────────────────────────────────────────────────────────────
    // Diese Liste ist kein Anhang, sondern Teil des Ergebnisses: eine Matrix
    // ohne sie sieht vollstaendig aus, obwohl ausgerechnet das ungeplante
    // Gewerk fehlt.

    ueberschrift(`Gewerke ohne verwertbaren Zeitraum (${daten.ohneZeitraum.length})`);
    if (daten.ohneZeitraum.length === 0) {
        // Die Entwarnung gilt NUR, wenn auch kein einzelner Arbeitsschritt ohne
        // Datum ist — sonst spricht das Blatt jedes Gewerk frei, obwohl ein Teil
        // des Plans gar nicht geprueft wurde.
        absatz(
            luecke.length === 0
                ? 'Alle Gewerke des Plans haben einen vollständigen Zeitraum.'
                : 'Kein Gewerk fällt ganz aus der Prüfung. Einzelne Arbeitsschritte haben aber kein '
                  + 'Datum — siehe den nächsten Abschnitt.',
            'italic',
        );
        y += 4;
    } else {
        absatz(
            'Diese Gewerke nehmen an der Überschneidungsprüfung NICHT teil. Ihr Fehlen in der Matrix ist '
            + 'kein Befund, sondern eine Lücke im Terminplan.',
            'italic',
            SCHRIFT.klein,
        );
        y += 2;
        autoTable(doc, {
            ...tabellenStil,
            startY: y,
            head: [['Gewerk', 'Arbeitsschritte', 'Grund']],
            body: daten.ohneZeitraum.map(a => [
                a.gewerk.label,
                String(a.gewerk.anzahlSchritte),
                a.hinweis,
            ]),
            columnStyles: {
                0: { cellWidth: 45 },
                1: { cellWidth: 24, halign: 'center' },
                2: { cellWidth: 'auto' },
            },
        });
        nachTabelle();
    }

    // ─────────────────────────────────────────────────────────────────────
    // 5b) Gewerke mit Arbeitsschritten ohne Datum
    // ─────────────────────────────────────────────────────────────────────
    // Der gefaehrlichere Fall als 5): diese Gewerke STEHEN in der Matrix und
    // sehen dort vollstaendig aus. Gerechnet wurde aber nur die Huelle ihrer
    // datierten Schritte — ein Schritt "ab 20.09., Ende offen" zieht sie nicht
    // auf und faellt in keine Ausschussliste. Ohne diesen Abschnitt erschiene
    // eine echte Ueberschneidung als geprueft und unbedenklich.

    if (luecke.length > 0) {
        ueberschrift(`Gewerke mit Arbeitsschritten ohne Datum (${luecke.length})`);
        absatz(
            'Diese Gewerke nehmen an der Prüfung teil — aber nur mit ihren datierten Arbeitsschritten. '
            + 'Der unten genannte Zeitraum umfasst die undatierten Schritte NICHT; für sie ist die '
            + 'Überschneidungsprüfung nicht gelaufen. Eine leere Matrixzelle ist bei diesen Gewerken '
            + 'deshalb kein Nachweis der Gefährdungsfreiheit.',
            'italic',
            SCHRIFT.klein,
        );
        y += 2;
        autoTable(doc, {
            ...tabellenStil,
            startY: y,
            head: [['Gewerk', 'Arbeitsschritte', 'davon ohne Datum', 'Gerechneter Zeitraum']],
            body: luecke.map(g => [
                g.label,
                String(g.anzahlSchritte),
                String(g.schritteOhneDatum),
                `${tag(g.von)} – ${tag(g.bis)}`,
            ]),
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 26, halign: 'center' },
                2: { cellWidth: 30, halign: 'center' },
                3: { cellWidth: 40 },
            },
        });
        nachTabelle();
    }

    // ─────────────────────────────────────────────────────────────────────
    // 6) Gewerke ohne Zuordnung
    // ─────────────────────────────────────────────────────────────────────

    if (daten.offeneGewerke.length > 0) {
        ueberschrift(`Gewerke ohne Zuordnung (${daten.offeneGewerke.length})`);
        absatz(
            'Der Katalog konnte diese Bezeichnungen keinem Gewerk zuordnen. Sie stehen in der Matrix, '
            + 'bekommen aber keine Regelvorschläge. Ein nicht zugeordnetes Gewerk ist KEIN Nachweis der '
            + 'Gefährdungsfreiheit — bis zur Klärung gilt jede Überschneidung mit ihm als ungeprüft.',
            'italic',
            SCHRIFT.klein,
        );
        y += 2;
        absatz(daten.offeneGewerke.map(g => g.label).join(' · '));
        y += 5;
    }

    // ─────────────────────────────────────────────────────────────────────
    // 7) Was allein aus der Gleichzeitigkeit folgt
    // ─────────────────────────────────────────────────────────────────────

    if (daten.hinweise.length > 0) {
        ueberschrift('Was allein aus der Gleichzeitigkeit folgt');
        daten.hinweise.forEach(h => {
            platzFuer(ZEILE.text * 3);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(SCHRIFT.block);
            doc.setTextColor(tp0, tp1, tp2);
            (doc.splitTextToSize(`${h.regel.code} · ${h.regel.hinweis}`, contentWidth) as string[]).forEach(z => {
                platzFuer(ZEILE.text);
                doc.text(z, margin, y);
                y += ZEILE.text;
            });
            absatz(h.begruendung);
            absatz(h.regel.massnahme);
            if (h.regel.vorschriften.length > 0) {
                absatz(h.regel.vorschriften.join(' · '), 'italic', SCHRIFT.klein);
            }
            y += 3;
        });
    }

    // ─────────────────────────────────────────────────────────────────────
    // 8) Schlussvermerk
    // ─────────────────────────────────────────────────────────────────────

    platzFuer(ZEILE.text * 4);
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightX, y);
    y += 4;
    absatz(
        'Grundlage dieser Auswertung sind die Zeiträume der Arbeitsschritte der neuesten, nicht '
        + 'gelöschten Fassung des SiGe-Plans. Die zeitliche Überschneidung ist gerechnet; die Bewertung '
        + 'und die koordinierende Maßnahme stammen vom Sicherheits- und Gesundheitsschutzkoordinator. '
        + 'Wo eine Paarung nur bei Arbeiten übereinander oder im selben Bereich gilt, wurde das nicht '
        + 'maschinell entschieden — die räumliche Zuordnung am Arbeitsschritt ist Freitext.',
        'italic',
        SCHRIFT.klein,
    );

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);
};

/**
 * Der Koordinierungsbericht als jsPDF-Dokument.
 *
 * Gibt bewusst das Dokument zurueck und keine Bytes: der Aufrufer entscheidet,
 * ob er speichert, anhaengt oder in die Vorschau haengt.
 */
export const generateKoordinierungPdf = (
    daten: KoordinierungPdfDaten,
    company: CompanyInfo,
    optionen?: KoordinierungPdfOptionen,
): jsPDF => {
    const doc = optionen && optionen.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    // Letzte Rueckfallebene fuer alles, was nicht durch `einzeilig()` gelaufen
    // ist — auch fuer das, was `letterhead.ts` und `jspdf-autotable` von innen
    // heraus schreiben.
    sichereTextausgabe(doc);
    renderKoordinierung(doc, daten, company || {}, optionen);
    return doc;
};

/**
 * Dateiname des Berichts.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage-Bucket, und dort ist ein Schraegstrich aus einem
 * Projektnamen ein neuer Ordner.
 */
export const koordinierungDateiname = (projektName: string, datumIso?: string): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['Koordinierung', sauber(projektName)];
    teile.push((datumIso || '').slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};
